import { ApiRouteService } from '../services';
import { HttpClient } from './HttpClient';

export interface FileTaggingRule {
  id: number;
  name: string;
  description: string;
  active: boolean;
  priority: number;
  filterExpression: string;
  liquidTemplate: string;
  worklistViewId?: number;
  assignedTags: FileTaggingRuleTag[];
  dateCreated?: string;
  dateModified?: string;
  userCreated?: string;
  userModified?: string;
}

export interface FileTaggingRuleTag {
  id: number;
  fileTaggingRuleId: number;
  tagId: number;
  tag: TagModel;
}

export interface TagModel {
  id: number;
  name: string;
  description: string;
  required: boolean;
  multipleSection: boolean;
  parentTagId?: number;
}

export interface TestTaggingRuleRequest {
  examSUID: string;
}

export interface ApplyTaggingRulesRequest {
  examSUID: string;
  patientFileId: number;
  executionPhase: string;
}

export interface TestTaggingRuleResponse {
  wouldApplyTags: boolean;
}

export interface ApplyTaggingRulesResponse {
  success: boolean;
}

export class FileTaggingClient {
  constructor(private httpClient: HttpClient) {
    this.getFileTaggingRules = this.getFileTaggingRules.bind(this);
    this.getFileTaggingRule = this.getFileTaggingRule.bind(this);
    this.createFileTaggingRule = this.createFileTaggingRule.bind(this);
    this.updateFileTaggingRule = this.updateFileTaggingRule.bind(this);
    this.deleteFileTaggingRule = this.deleteFileTaggingRule.bind(this);
    this.testFileTaggingRule = this.testFileTaggingRule.bind(this);
    this.applyTaggingRules = this.applyTaggingRules.bind(this);
  }

  async getFileTaggingRules(activeOnly = true): Promise<FileTaggingRule[]> {
    const options = await this.httpClient.createStandardOptions('GET', null);
    const url = `${ApiRouteService.getCompumedApiBaseRoute()}/api/FileTagging/rules?activeOnly=${activeOnly}`;
    return this.httpClient.fetchAndParse<FileTaggingRule[]>(fetch(url, options));
  }

  async getFileTaggingRule(id: number): Promise<FileTaggingRule> {
    const options = await this.httpClient.createStandardOptions('GET', null);
    const url = `${ApiRouteService.getCompumedApiBaseRoute()}/api/FileTagging/rules/${id}`;
    return this.httpClient.fetchAndParse<FileTaggingRule>(fetch(url, options));
  }

  async createFileTaggingRule(rule: Partial<FileTaggingRule>): Promise<FileTaggingRule> {
    const options = await this.httpClient.createStandardOptions('POST', rule);
    const url = `${ApiRouteService.getCompumedApiBaseRoute()}/api/FileTagging/rules`;
    return this.httpClient.fetchAndParse<FileTaggingRule>(fetch(url, options));
  }

  async updateFileTaggingRule(id: number, rule: Partial<FileTaggingRule>): Promise<FileTaggingRule> {
    const options = await this.httpClient.createStandardOptions('PUT', rule);
    const url = `${ApiRouteService.getCompumedApiBaseRoute()}/api/FileTagging/rules/${id}`;
    return this.httpClient.fetchAndParse<FileTaggingRule>(fetch(url, options));
  }

  async deleteFileTaggingRule(id: number): Promise<void> {
    const options = await this.httpClient.createStandardOptions('DELETE', null);
    const url = `${ApiRouteService.getCompumedApiBaseRoute()}/api/FileTagging/rules/${id}`;
    await this.httpClient.fetch(fetch(url, options));
  }

  async testFileTaggingRule(id: number, request: TestTaggingRuleRequest): Promise<TestTaggingRuleResponse> {
    const options = await this.httpClient.createStandardOptions('POST', request);
    const url = `${ApiRouteService.getCompumedApiBaseRoute()}/api/FileTagging/rules/${id}/test`;
    return this.httpClient.fetchAndParse<TestTaggingRuleResponse>(fetch(url, options));
  }

  async applyTaggingRules(request: ApplyTaggingRulesRequest): Promise<ApplyTaggingRulesResponse> {
    const options = await this.httpClient.createStandardOptions('POST', request);
    const url = `${ApiRouteService.getCompumedApiBaseRoute()}/api/FileTagging/apply`;
    return this.httpClient.fetchAndParse<ApplyTaggingRulesResponse>(fetch(url, options));
  }
}
